import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { navigateToModuleScreen } from '@/common/navigation/RoleNavigations/Student/navigateToModuleScreen';
import type { StudentRouteParamList } from '@/common/navigation/RoleNavigations/Student/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  VStack,
  ScrollView,
  Button,
  ButtonText,
  Spinner,
  Text,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useResetMcqAttempt } from '@/common/api/endpoints/mcq-submissions/mcq-submissions';
import { useCreateTaskSubmission } from '@/common/api/endpoints/submissions/submissions';
import { getGetTasksByCourseModuleQueryKey } from '@/common/api/endpoints/tasks/tasks';
import {
  getGetModulesByCourseQueryKey,
  getGetModulesQueryKey,
} from '@/common/api/endpoints/modules/modules';
import { getGetCourseQueryKey } from '@/common/api/endpoints/courses/courses';
import { useDialog } from '@/common/context/DialogContext';
import type { Task } from '@/common/api/models/task';
import type { SubmissionMcqResult } from '@/common/api/models';
import { QuestionCard } from './QuestionCard';
import { QuizProgress } from './QuizProgress';
import { QuizResults } from './QuizResults';
import { WaitingForExam } from './WaitingForExam';
import { TimerDisplay } from './TimerDisplay';
import { ExamClosedMessage } from './ExamClosedMessage';
import { useMCQQuiz } from './hooks/useMCQQuiz';
import type { MCQQuestion } from './types';

interface MCQComposerProps {
  courseId: string;
  moduleId: string;
  taskId: string;
  version?: string;
  slotId?: string;
  readOnly?: boolean;
  submissionMcqResult?: SubmissionMcqResult | null;
  showImmediateFeedback?: boolean;
}

export const MCQComposer: React.FC<MCQComposerProps> = ({
  courseId,
  moduleId,
  taskId,
  version,
  slotId,
  readOnly = false,
  submissionMcqResult,
  showImmediateFeedback,
}) => {
  const { t } = useTranslation();
  const navigation = useNavigation<NativeStackNavigationProp<StudentRouteParamList>>();
  const queryClient = useQueryClient();
  const { showToastMessage, showDialog, hideDialog } = useDialog();
  const state = useMCQQuiz({
    courseId,
    moduleId,
    taskId,
    version,
    slotId,
    submissionMcqResult,
    showImmediateFeedback,
    readOnly,
  });
  const [autoSubmitted, setAutoSubmitted] = useState(false);
  const autoSubmitRef = useRef(false);
  const completionDialogShownRef = useRef(false);
  const coldReplayToastShownRef = useRef(false);
  const resetAttempt = useResetMcqAttempt();
  const markDoneMutation = useCreateTaskSubmission();
  const normalizedVersion = version || 'latest';
  const [localTimeRemainingSeconds, setLocalTimeRemainingSeconds] = useState<number | null>(null);
  const moduleTasksBaseQueryKey = getGetTasksByCourseModuleQueryKey(courseId, moduleId);
  const moduleTasksSubmissionQueryKey = getGetTasksByCourseModuleQueryKey(
    courseId,
    moduleId,
    { include: 'submissions,resources' },
  );

  const examStatus = state.examStatus ?? null;
  const isProctored = Boolean(slotId);

  const invalidateCourseCaches = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getGetModulesQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetModulesByCourseQueryKey(courseId) });
    queryClient.invalidateQueries({ queryKey: getGetCourseQueryKey(courseId) });
    queryClient.invalidateQueries({ queryKey: moduleTasksSubmissionQueryKey });
    queryClient.invalidateQueries({ queryKey: moduleTasksBaseQueryKey });
  }, [queryClient, courseId, moduleTasksSubmissionQueryKey, moduleTasksBaseQueryKey]);

  const navigateToCourse = useCallback(() => {
    invalidateCourseCaches();
    navigation.navigate('CourseScreen');
  }, [invalidateCourseCaches, navigation]);

  useEffect(() => {
    if (examStatus?.timeRemainingSeconds == null && !examStatus?.autoCloseAt) {
      setLocalTimeRemainingSeconds(null);
      return undefined;
    }
    const computeRemainingFromCloseAt = () => {
      const endMs = new Date(examStatus.autoCloseAt as string).getTime();
      return Math.max(0, Math.floor((endMs - Date.now()) / 1000));
    };
    const seed = examStatus?.timeRemainingSeconds ?? computeRemainingFromCloseAt();
    setLocalTimeRemainingSeconds(seed);
    const timer = setInterval(() => {
      setLocalTimeRemainingSeconds((prev) => {
        if (prev == null) return 0;
        return Math.max(0, prev - 1);
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [examStatus?.timeRemainingSeconds, examStatus?.autoCloseAt]);

  // Auto-submit when timer expires (including partial answers).
  useEffect(() => {
    if (
      state.status === 'ready'
      && localTimeRemainingSeconds != null
      && localTimeRemainingSeconds <= 0
      && !autoSubmitRef.current
    ) {
      autoSubmitRef.current = true;
      setAutoSubmitted(true);
      state.submit({ force: true, flush: true });
    }
  }, [state, localTimeRemainingSeconds]);

  useEffect(() => {
    if (state.status === 'cold_replay_failed' && !coldReplayToastShownRef.current) {
      coldReplayToastShownRef.current = true;
      showToastMessage('error', t('student.mcq.coldReplayFailed'));
      return;
    }
    if (state.status !== 'cold_replay_failed') {
      coldReplayToastShownRef.current = false;
    }
  }, [state.status, showToastMessage, t]);

  // Exam status polling is handled by React Query refetchInterval in useMCQQuiz.
  // When exam opens, the questions query enables automatically.

  const navigateBackToModule = useCallback(() => {
    if (typeof navigation.canGoBack === 'function' && navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigateToModuleScreen(navigation, courseId, moduleId);
  }, [navigation, courseId, moduleId]);

  const clearParentMcqResult = useCallback(() => {
    const clearMcqResultFromTasks = (prev: unknown) => {
      const typedPrev = prev as { data?: { data?: Task[] } } | undefined;
      const previousTasks = typedPrev?.data?.data;
      if (!previousTasks) return prev;
      return {
        ...typedPrev,
        data: {
          ...(typedPrev?.data || {}),
          data: previousTasks.map((task) => (
            task.id === taskId
              ? {
                ...task,
                submission: task.submission
                  ? { ...task.submission, mcqResult: undefined }
                  : task.submission,
              }
              : task
          )),
        },
      };
    };

    queryClient.setQueryData(moduleTasksSubmissionQueryKey, clearMcqResultFromTasks);
    queryClient.setQueriesData(
      {
        predicate: (query) => query.queryKey?.[0] === moduleTasksBaseQueryKey[0],
      },
      clearMcqResultFromTasks,
    );
  }, [queryClient, moduleTasksSubmissionQueryKey, moduleTasksBaseQueryKey, taskId]);

  const handleRetry = async () => {
    try {
      await resetAttempt.mutateAsync({
        courseId,
        moduleId,
        taskId,
        params: { version: normalizedVersion },
      });
      clearParentMcqResult();
      await state.resetForRetry();
      setAutoSubmitted(false);
      autoSubmitRef.current = false;
    } catch {
      showToastMessage('error', t('student.mcq.retakeDialog.resetFailed'));
    }
  };

  const syncModuleTaskDoneState = useCallback(() => {
    queryClient.setQueryData(moduleTasksSubmissionQueryKey, (prev: unknown) => {
      const typedPrev = prev as { data?: { data?: Task[] } } | undefined;
      const previousTasks = typedPrev?.data?.data;
      if (!previousTasks) return prev;
      return {
        ...typedPrev,
        data: {
          ...(typedPrev?.data || {}),
          data: previousTasks.map((task) => (
            task.id === taskId
              ? {
                ...task,
                submission: {
                  ...(task.submission || {}),
                  taskId,
                  state: 'marked_as_done',
                },
              }
              : task
          )),
        },
      };
    });
    queryClient.setQueriesData(
      {
        predicate: (query) => query.queryKey?.[0] === moduleTasksBaseQueryKey[0],
      },
      (prev: unknown) => {
        const typedPrev = prev as { data?: { data?: Task[] } } | undefined;
        const previousTasks = typedPrev?.data?.data;
        if (!previousTasks) return prev;
        return {
          ...typedPrev,
          data: {
            ...(typedPrev?.data || {}),
            data: previousTasks.map((task) => (
              task.id === taskId
                ? {
                  ...task,
                  submission: {
                    ...(task.submission || {}),
                    taskId,
                    state: 'marked_as_done',
                  },
                }
                : task
            )),
          },
        };
      },
    );
    queryClient.invalidateQueries({ queryKey: moduleTasksSubmissionQueryKey });
    queryClient.invalidateQueries({ queryKey: moduleTasksBaseQueryKey });
  }, [queryClient, moduleTasksSubmissionQueryKey, moduleTasksBaseQueryKey, taskId]);

  useEffect(() => {
    if (!isProctored || state.status !== 'submitted' || completionDialogShownRef.current) return;
    completionDialogShownRef.current = true;
    syncModuleTaskDoneState();
    showDialog({
      title: autoSubmitted ? t('student.mcq.timeoutSubmittedTitle') : t('student.mcq.submittedTitle'),
      message: autoSubmitted ? t('student.mcq.timeoutSubmittedMessage') : t('student.mcq.submittedMessage'),
      isPersistent: true,
      buttons: [
        {
          text: t('student.mcq.backToCourse'),
          action: 'positive',
          onPress: () => {
            hideDialog();
            navigateToCourse();
          },
        },
      ],
    });
  }, [
    isProctored,
    state.status,
    autoSubmitted,
    showDialog,
    hideDialog,
    t,
    syncModuleTaskDoneState,
    navigateToCourse,
  ]);

  const handleMarkAsDone = useCallback(() => {
    markDoneMutation.mutate(
      {
        courseId,
        moduleId,
        taskId,
        data: {
          text: '',
          state: 'marked_as_done',
        },
      },
      {
        onSuccess: () => {
          syncModuleTaskDoneState();
          navigateBackToModule();
        },
        onError: () => showToastMessage('error', t('student.mcq.markDoneError')),
      },
    );
  }, [
    markDoneMutation,
    courseId,
    moduleId,
    taskId,
    syncModuleTaskDoneState,
    navigateBackToModule,
    showToastMessage,
    t,
  ]);

  if (markDoneMutation.isPending) {
    return (
      <Box testID="mcq-loading-indicator" flex={1} justifyContent="center" alignItems="center" p="$6">
        <Spinner size="large" />
        <Text mt="$4" color="$textSubtle">
          {t('student.mcq.retakeDialog.preparing')}
        </Text>
      </Box>
    );
  }

  // Handle loading state
  if (state.status === 'loading') {
    return (
      <Box testID="mcq-loading-indicator" flex={1} justifyContent="center" alignItems="center" p="$6">
        <Spinner size="large" />
        <Text mt="$4" color="$textSubtle">{t('student.mcq.loadingQuiz')}</Text>
      </Box>
    );
  }

  // Handle error state
  if (state.status === 'error') {
    return (
      <Box testID="mcq-error-message" flex={1} justifyContent="center" alignItems="center" p="$6">
        <Text color="$error600" textAlign="center" mb="$4">
          {state.error.message || t('student.mcq.loadFailed')}
        </Text>
        <Button testID="mcq-retry-button" onPress={state.retry}>
          <ButtonText>{t('student.mcq.retry')}</ButtonText>
        </Button>
      </Box>
    );
  }

  if (state.status === 'cold_replay_failed') {
    return (
      <Box testID="mcq-cold-replay-failed" flex={1} justifyContent="center" alignItems="center" p="$6">
        <Text color="$error600" textAlign="center" mb="$4">
          {state.error.message || t('student.mcq.coldReplayFailed')}
        </Text>
        <Button testID="mcq-cold-replay-retry-button" onPress={state.retryColdReplay}>
          <ButtonText>{t('student.mcq.retry')}</ButtonText>
        </Button>
      </Box>
    );
  }

  // Handle waiting for exam state
  if (state.status === 'waiting_for_exam') {
    return <WaitingForExam />;
  }

  if (state.status === 'waiting_for_admission') {
    return (
      <Box testID="mcq-waiting-for-admission-message" flex={1} justifyContent="center" alignItems="center" p="$6">
        <VStack space="md" alignItems="center">
          <Text color="$textSubtle" textAlign="center">
            {t('student.mcq.waitingForAdmission')}
          </Text>
          <Text color="$textSubtle" textAlign="center">
            {t('student.mcq.waitForLecturerVerification')}
          </Text>
          <Button
            testID="mcq-waiting-for-admission-back-button"
            onPress={() => navigateToModuleScreen(navigation, courseId, moduleId)}
          >
            <ButtonText>{t('student.mcq.backToModule')}</ButtonText>
          </Button>
        </VStack>
      </Box>
    );
  }

  if (state.status === 'needs_booking') {
    return (
      <Box testID="mcq-needs-booking-message" flex={1} justifyContent="center" alignItems="center" p="$6">
        <VStack space="md" alignItems="center">
          <Text color="$textSubtle" textAlign="center">
            {t('student.mcq.needsBookingMessage')}
          </Text>
          <Button
            testID="mcq-needs-booking-back-button"
            onPress={() => navigateToModuleScreen(navigation, courseId, moduleId)}
          >
            <ButtonText>{t('student.mcq.backToModule')}</ButtonText>
          </Button>
        </VStack>
      </Box>
    );
  }

  // Handle exam closed state
  if (state.status === 'exam_closed') {
    return <ExamClosedMessage />;
  }

  // Handle submitting state
  if (state.status === 'submitting') {
    const isAutoSubmit = examStatus?.timeRemainingSeconds != null
      && examStatus.timeRemainingSeconds <= 0;

    if (isAutoSubmit || autoSubmitted) {
      return (
        <Box testID="mcq-auto-submit-message" flex={1} justifyContent="center" alignItems="center" p="$6">
          <Spinner size="large" />
          <Text mt="$4" color="$textSubtle" textAlign="center">
            {t('student.mcq.autoSubmitted')}
          </Text>
        </Box>
      );
    }
    return (
      <Box testID="mcq-submitting-indicator" flex={1} justifyContent="center" alignItems="center" p="$6">
        <Spinner size="large" />
        <Text mt="$4" color="$textSubtle">{t('student.mcq.submittingAnswers')}</Text>
      </Box>
    );
  }

  // Handle submitted state
  if (state.status === 'submitted') {
    const { results } = state;
    const hideResults = results.immediateFeedback === false;
    if (hideResults) {
      const showDoneButton = !isProctored && !readOnly;
      if (isProctored) {
        return (
          <Box testID="mcq-submitted-proctored-redirecting" flex={1} justifyContent="center" alignItems="center" p="$6">
            <Spinner size="large" />
            <Text mt="$4" color="$textSubtle">
              {t('student.mcq.finalizingSubmission')}
            </Text>
          </Box>
        );
      }
      return (
        <Box testID="mcq-submitted-no-feedback-screen" flex={1} justifyContent="center" alignItems="center" p="$6">
          <VStack space="md" alignItems="center">
            <Text color="$textBody" fontWeight="semibold" textAlign="center">
              {t('student.mcq.submissionSavedTitle')}
            </Text>
            <Text color="$textSubtle" textAlign="center">
              {isProctored
                ? t('student.mcq.submissionSavedExamMessage')
                : t('student.mcq.noFeedbackMessage')}
            </Text>
            {showDoneButton && (
              <Button testID="mcq-no-feedback-done-button" onPress={handleMarkAsDone} mt="$2">
                <ButtonText>{t('student.mcq.markAsDone')}</ButtonText>
              </Button>
            )}
          </VStack>
        </Box>
      );
    }
    const showRetry = !results.passed;
    const showMarkAsDone = results.passed;

    return (
      <Box testID="mcq-results-screen" flex={1}>
        <ScrollView>
          <VStack space="md" p="$4">
            <QuizResults
              result={results}
              onAction={showRetry ? handleRetry : handleMarkAsDone}
              actionLabel={showRetry ? t('student.mcq.retakeDialog.confirm') : t('student.mcq.markAsDone')}
              showAction={showRetry || showMarkAsDone}
            />
          </VStack>
        </ScrollView>
      </Box>
    );
  }

  // Handle ready state
  if (state.status === 'ready') {
    const { questions, answers } = state;
    const answeredCount = state.getAnsweredCount();
    const allAnswered = questions.length > 0 && answeredCount === questions.length;

    if (questions.length === 0) {
      return (
        <Box flex={1} justifyContent="center" alignItems="center" p="$6">
          <Text color="$textSubtle" textAlign="center">
            {t('student.mcq.noQuestions')}
          </Text>
        </Box>
      );
    }

    return (
      <VStack flex={1}>
        {examStatus && (
          <Box px="$4" pt="$3" pb="$2" bg="$bgCard" borderBottomWidth={1} borderBottomColor="$borderLight200">
            <TimerDisplay
              examStatus={{
                ...examStatus,
                timeRemainingSeconds: localTimeRemainingSeconds ?? examStatus.timeRemainingSeconds ?? undefined,
              }}
              compact
            />
          </Box>
        )}
        <ScrollView>
          <VStack space="md" p="$4">
            <QuizProgress
              totalQuestions={questions.length}
              answeredCount={answeredCount}
              timeRemaining={null}
            />

            {questions.map((question: MCQQuestion, index: number) => (
              <View testID="mcq-question-card" key={question.id}>
                <QuestionCard
                  question={question}
                  questionNumber={index + 1}
                  selectedAnswer={answers[question.id]}
                  onSelectAnswer={(optionId: string) => state.selectAnswer(question.id, optionId)}
                  showResults={false}
                  isDisabled={readOnly}
                />
              </View>
            ))}

            {!readOnly && (
              <>
                <Button
                  testID="mcq-submit-button"
                  size="lg"
                  onPress={() => {
                    state.submit();
                  }}
                  disabled={!allAnswered}
                  isDisabled={!allAnswered}
                  bg={allAnswered ? '$primary500' : '$bgSubtle'}
                >
                  <ButtonText>{t('student.mcq.submitQuiz')}</ButtonText>
                </Button>

                {!allAnswered && (
                  <Text size="sm" color="$textSubtle" textAlign="center">
                    {t('student.mcq.answerAllQuestions')}
                  </Text>
                )}
              </>
            )}
          </VStack>
        </ScrollView>
      </VStack>
    );
  }

  // This should never happen, but TypeScript requires exhaustive checking
  return null;
};

export default MCQComposer;
